import { useState, useRef, useCallback, type KeyboardEvent, type ChangeEvent } from "react";
import { Send, Paperclip, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACCEPT = "image/*,application/pdf,.txt,.md,.json,.csv,.ts,.tsx,.js,.jsx,.py,.html,.css";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

type ImageAttachment = {
  base64: string;
  mediaType: string;
  name: string;
  preview: string; // data URL for display
};

type Props = {
  onSend: (message: string, images?: Array<{ base64: string; mediaType: string; name: string }>) => void;
  disabled?: boolean;
};

export function SessionInput({ onSend, disabled }: Props) {
  const [value, setValue] = useState("");
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed && images.length === 0) return;

    const imageData = images.length > 0
      ? images.map(({ base64, mediaType, name }) => ({ base64, mediaType, name }))
      : undefined;

    onSend(trimmed || "What's in this image?", imageData);
    setValue("");
    setImages([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, images, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) continue;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        // Extract base64 from data URL: "data:image/png;base64,XXXX"
        const base64 = dataUrl.split(",")[1];
        setImages((prev) => [
          ...prev,
          { base64, mediaType: file.type, name: file.name, preview: dataUrl },
        ]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div
      className="border-t border-zinc-800 bg-zinc-900 px-3 py-3 relative z-10"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      {/* Image previews */}
      {images.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <img
                src={img.preview}
                alt={img.name}
                className="h-16 rounded border border-zinc-700"
              />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute -top-1.5 -right-1.5 bg-zinc-900 rounded-full"
              >
                <XCircle className="h-4 w-4 text-zinc-400 hover:text-zinc-200" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 min-w-0">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 h-9 w-9 p-0 text-zinc-400 hover:text-zinc-200"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            handleInput();
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Send a message..."
          rows={1}
          className="flex-1 min-w-0 resize-none rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          style={{ maxHeight: 120 }}
        />
        <Button
          size="sm"
          onClick={handleSend}
          disabled={disabled || (!value.trim() && images.length === 0)}
          className="shrink-0 h-9 w-9 p-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
